/**
 * Every key this app binds, one line each.
 *
 * The bindings were discoverable one at a time — a letter on an inspector row, a
 * chord in the empty state, a sentence in the guide — and nowhere at once, so the
 * only way to learn the second half of them was to read the source. This is that
 * list, on the key it is named after.
 *
 * ONE LINE PER CONTROL, saying what it does. The sheet used to carry a paragraph
 * of reasoning under most rows — the guard each key obeys, the key it displaced,
 * the bug that moved it — and a usability study measured the result at some 2,500
 * words of design history in a reference people open to find one key. That
 * reasoning was not wrong and none of it was dropped: it is in docs/guide.md,
 * under "The keyboard", grouped the way this sheet is. What stays here is the
 * caveat a user needs at the moment of pressing, said once on the row it affects:
 * that single letters wait while you type or a sheet is up (the Everywhere note),
 * that Escape never stops a run (its row), and which keys spend.
 *
 * Two things this sheet keeps as contract, whatever its copy becomes:
 *
 *  - **The money is marked.** Nine of these keys start a paid pass. Every other
 *    path in this app prices itself before the press — the buttons on hover, the
 *    tuner on the composer, the toast the keystroke itself fires — and a reference
 *    that listed them beside Fit and Fold would flatten the one distinction the
 *    product is most careful about.
 *  - **Escape must never be the thing that stops something costing money.** A live
 *    run is not on the Escape ladder; Stop on the composer, or Space while a run
 *    is going, is how a run ends. The Esc row says so in four words.
 *
 * Grouped by what someone is trying to do, not by keycap, because that is how the
 * question arrives: "how do I get back", not "what does Z do".
 */

import { useRef, type CSSProperties, type ReactNode } from 'react'
import { useStore } from '../core/store'
import { Icon } from './components/Icon'
import { useModalFocus } from './components/useModalFocus'

/**
 * One binding: chips in a fixed first column, the sentence in the elastic one.
 *
 * Fixed rather than content-sized, so every key in the sheet lines up down a
 * single edge — a reference is scanned by keycap first, and a left column that
 * moved with the width of each chord would turn that scan into a search.
 */
const ROW: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '86px minmax(0, 1fr)',
  alignItems: 'baseline',
  gap: 'var(--s3)',
}

/** The sentence itself, at the sheet's body size. */
const DOES: CSSProperties = { fontSize: 'var(--fs-small)', lineHeight: 1.5, color: 'var(--text-1)' }

function Row({
  keys,
  does,
  spends,
}: {
  keys: readonly string[]
  does: string
  /** Marks the nine keys that start a paid pass. */
  spends?: boolean
}) {
  return (
    <div style={ROW}>
      <span style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {keys.map((k) => (
          <span key={k} className="kbd">
            {k}
          </span>
        ))}
      </span>
      <span style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--s2)' }}>
        <span style={DOES}>{does}</span>
        {spends && (
          <span className="eyebrow" style={{ flex: 'none', color: 'var(--text-2)' }}>
            Spends
          </span>
        )}
      </span>
    </div>
  )
}

/**
 * A pointer gesture, which gets a sentence rather than a chip.
 *
 * "Double-click" does not fit a keycap and does not belong in one: `.kbd` is
 * shaped for a key that exists on a keyboard. The guide already writes these
 * gestures as bolded words in a line, so this reads the same way it does there.
 */
function Move({ gesture, does }: { gesture: string; does: string }) {
  return (
    <span style={DOES}>
      <b style={{ fontWeight: 590 }}>{gesture}</b> {does}
    </span>
  )
}

function Section({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    // Rows at 8px inside a section against the sheet body's 16px between them:
    // twice the gap is what makes the grouping readable without a rule per
    // heading, and this sheet would otherwise be nine hairlines tall.
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s2)' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span className="eyebrow">{title}</span>
        {note && <span className="gnote">{note}</span>}
      </div>
      {children}
    </div>
  )
}

export function ShortcutsSheet() {
  const open = useStore((s) => s.shortcutsOpen)
  const setOpen = useStore((s) => s.setShortcuts)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  useModalFocus(dialogRef, open)

  if (!open) return null

  return (
    <div
      className="scrim"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) setOpen(false)
      }}
    >
      <div
        className="sheet sheet--tall panel"
        role="dialog"
        aria-modal="true"
        aria-label="Keyboard shortcuts"
        ref={dialogRef}
        tabIndex={-1}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Icon name="command" size={18} strokeWidth={1.5} style={{ color: 'var(--accent)' }} />
          <h2>Keyboard shortcuts</h2>
        </div>

        <div className="sheet-body scroll">
          <Section
            title="Everywhere"
            note="⌘ and Ctrl are the same key here, and ⌥ is Alt. Single letters act only with nothing focused, or with focus on the canvas or a node — never while a field, a button or a sheet has it."
          >
            <Row keys={['⌘K']} does="Open or close the command palette." />
            <Row keys={['?']} does="Open or close this sheet." />
            <Row
              keys={['⌘N']}
              does="Start a new exploration. Some browsers keep ⌘N for a new window."
            />
            <Row keys={['⌘[']} does="Collapse the explorations sidebar to its rail, or open it." />
            <Row keys={['⌘]']} does="Show or hide the thread." />
            <Row keys={['⌘Z']} does="Undo the last change to the graph." />
            <Row keys={['⇧⌘Z', '⌘Y']} does="Redo it." />
            <Row keys={['Esc']} does="Close the top layer, one per press. It never stops a run." />
            <Row keys={['Tab', '⇧Tab']} does="Move through an open sheet." />
          </Section>

          <Section
            title="On the selected node"
            note="Select a node first. Keys marked Spends start a paid pass and show its estimate as they fire. Pressed while a pass is running, a move, U, V or P waits on an Up next line (two at most) and runs when the pass lands; ✕ or Stop clears it."
          >
            <Row keys={['D']} does="Deepen" spends />
            <Row keys={['C']} does="Challenge" spends />
            <Row keys={['X']} does="Diverge" spends />
            <Row keys={['T']} does="Test" spends />
            <Row keys={['I']} does="Invert" spends />
            <Row keys={['B']} does="Branch" spends />
            <Row keys={['U']} does="Propose insights" spends />
            <Row
              keys={['V']}
              does="Check against sources — on the seed, survey the field; on an insight, search for prior art"
              spends
            />
            <Row keys={['P']} does="Ask three lenses separately" spends />
            <Row keys={['R']} does="Record what happened" />
            <Row keys={['E']} does="Rewrite this understanding" />
            <Row keys={['A']} does="Ask about this finding" />
          </Section>

          <Section title="The canvas">
            <Row keys={['F']} does="Fit the whole graph on screen." />
            <Row keys={['H']} does="Fold or unfold the selected branch." />
            <Row keys={['3']} does="Switch between the flat view and the spatial one." />
          </Section>

          <Section
            title="After a run — the coach"
            note="Apart from G and L, these work while the coach’s card is showing."
          >
            <Row keys={['G']} does="Open the coach, or put it away." />
            <Row keys={['J', '↓']} does="The next cue." />
            <Row keys={['K', '↑']} does="The previous cue." />
            <Row keys={['↵']} does="Take the cue’s action." spends />
            <Row keys={['S']} does="Skip this cue." />
            <Row keys={['W']} does="Show the rule that fired this cue." />
            <Row
              keys={['O']}
              does="Change the order: biggest risk, cheapest first, closest to a test."
            />
            <Row keys={['L']} does="Swap the pins on the graph for a list under the bar." />
            <Row keys={['Esc']} does="Put the card away, then the finished run, then the bar." />
          </Section>

          <Section title="Replay, and stopping a run">
            <Row
              keys={['Space']}
              does="Start or pause the replay. While a run is going, stop the run."
            />
            <Row keys={['↑', '↓']} does="On the step rail, move between steps." />
            <Row
              keys={['Home', 'End']}
              does="On the step rail, go to the first or the last step."
            />
            <Row keys={['↵']} does="On the step rail, replay the graph to that step." />
            <Row keys={['⇧B']} does="On the step rail, branch a new exploration from that step." />
          </Section>

          <Section title="The idea box">
            <Row
              keys={['↵']}
              does="Ask about the selected finding or the open graph — on a blank canvas, explore the idea."
            />
            <Row keys={['⌘↵']} does="Start a new exploration from what you typed." />
            <Row keys={['/']} does="Open the command completions." />
            <Row keys={['↑', '↓']} does="Walk the completions." />
          </Section>

          <Section title="The command palette">
            <Row keys={['↑', '↓']} does="Move the highlight." />
            <Row keys={['↵']} does="Run the highlighted row." />
            <Row keys={['Esc']} does="Close it." />
          </Section>

          <Section title="The explorations sidebar">
            <Row keys={['⌘[']} does="Collapse it to its rail, or open it." />
            <Row keys={['Tab', '⇧Tab']} does="Walk it." />
            <Row keys={['↑', '↓', 'Home', 'End']} does="Move between explorations." />
            <Row
              keys={['↵', 'Space']}
              does="Open the highlighted exploration, or the focused ⋮ menu."
            />
            <Row
              keys={['P', 'R', 'F', 'S']}
              does="Pin, rename, fork or share the focused exploration."
            />
            <Row
              keys={['⇧F10']}
              does="Open the focused exploration’s menu. A right-click on a row opens it too."
            />
            <Row keys={['M']} does="Pick the focused exploration up, to move it to a project." />
            <Row keys={['↑', '↓']} does="While one is picked up, choose where it goes." />
            <Row keys={['↵']} does="While one is picked up, drop it there." />
            <Row keys={['Esc']} does="While one is picked up, put it back." />
            <Row keys={['↵']} does="In the name field, keep the name." />
            <Row
              keys={['Esc']}
              does="In the name field, leave it as it was. In the model list, close it."
            />
            <Row keys={['Tab']} does="Reach the seam at the pane’s right edge." />
            <Row keys={['←', '→']} does="On the seam, narrow or widen the pane." />
            <Row keys={['Home', 'End']} does="On the seam, go to the narrowest or the widest." />
            <Row keys={['Esc']} does="While dragging the seam, put the pane back." />
          </Section>

          <Section title="The explorations search">
            <Row keys={['↑', '↓']} does="Walk the matches." />
            <Row keys={['↵']} does="Open the highlighted one." />
            <Row keys={['Esc']} does="Clear the box, then close the sheet." />
          </Section>

          <Section title="The thread column" note="Above the phone breakpoint.">
            <Row keys={['⌘]']} does="Fold the column away, or bring it back." />
            <Row keys={['Tab']} does="Reach the seam between the column and the field." />
            <Row keys={['←', '→']} does="On the seam, narrow or widen the column." />
            <Row keys={['Home', 'End']} does="On the seam, go to the narrowest or the widest." />
            <Row keys={['Esc']} does="While dragging the seam, put the column back." />
          </Section>

          <Section title="The other fields">
            <Row keys={['↵']} does="In the ask panel, send the question." />
            <Row keys={['⇧↵']} does="In the ask panel, start a new line instead." />
            <Row keys={['↵']} does="In a key field, save the key once there is one to save." />
          </Section>

          <Section title="The spatial view" note="Only while the spatial view is up.">
            <Row keys={['←', '→']} does="Orbit around the graph; ⇧ doubles the step." />
            <Row keys={['↑', '↓']} does="Orbit up and over, or down and under." />
            <Row keys={['⇧↑', '⇧↓']} does="Raise or lower the eye." />
            <Row keys={['+', '=']} does="Move closer." />
            <Row keys={['-']} does="Pull back." />
          </Section>

          <Section title="Pointer — the flat view">
            <Move gesture="Drag" does="pans the field." />
            <Move gesture="Wheel" does="zooms; a sideways swipe, or ⇧ with the wheel, pans." />
            <Move gesture="⌘ or Ctrl with the wheel" does="zooms faster, and so does a pinch." />
            <Move gesture="Click" does="selects a node and opens the thread on it." />
            <Move
              gesture="Double-click"
              does="a node isolates its branch, or releases it; on empty space, fits the graph."
            />
            <Move gesture="⌥-click" does="a node, or the strand into it, folds that branch." />
            {/* Touch has its own pinch now (D340): `touch-action: none` turns
                the browser's off, and the canvas handles two fingers itself. */}
            <Move
              gesture="Two fingers"
              does="on a touch screen, pinch to zoom and move together to pan."
            />
          </Section>

          <Section title="Pointer — the spatial view">
            <Move
              gesture="Drag"
              does="orbits; ⇧-drag pans, and ⌥-drag raises and lowers the eye."
            />
            <Move
              gesture="Wheel"
              does="dollies in and out, pinch included; ⇧ with the wheel pans."
            />
            <Move
              gesture="Double-click"
              does="a node flies to it and orbits around it; on empty space, re-frames the graph."
            />
            <Move
              gesture="Two fingers"
              does="on a touch screen, pinch to move closer or pull back, and move together to pan."
            />
          </Section>
        </div>

        <div className="sheet-actions">
          <button className="btn btn--primary" onClick={() => setOpen(false)}>
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
